const DOCK_MARGIN = 16;
const DOCK_GAP = 12;
const DOCK_CARD_WIDTH = 176;
const DOCK_CARD_HEIGHT = 108;
const MAX_SCALE = 0.32;
const MAX_SLOT_ORDER_CACHE_ENTRIES = 24;
export const MINIMIZED_DOCK_Z_INDEX_BASE = 0;
export const MIN_ACTIVE_WINDOW_Z_INDEX = MINIMIZED_DOCK_Z_INDEX_BASE + 1;

type DockSlot = {
  column: number;
  row: number;
  x: number;
  y: number;
};

const slotOrderCache = new Map<string, DockSlot[]>();

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const gridDistanceSquared = (left: DockSlot, right: DockSlot): number => {
  const columnDistance = left.column - right.column;
  const rowDistance = left.row - right.row;
  return columnDistance * columnDistance + rowDistance * rowDistance;
};

/**
 * Produces a stable, randomized-looking order that deliberately uses distant
 * grid cells first. The same workspace keeps the same arrangement across
 * renders, while every slot is still used exactly once before any are reused.
 */
const scatterDockSlots = (slots: DockSlot[], layoutKey: string): DockSlot[] => {
  if (slots.length <= 1) return slots;

  const seed = hashString(`minimized-dock:${layoutKey}`);
  const firstIndex = seed % slots.length;
  const selected = [slots[firstIndex]];
  const remaining = slots
    .filter((_, index) => index !== firstIndex)
    .map((slot) => ({
      slot,
      minDistance: gridDistanceSquared(slot, selected[0]),
      tieBreaker: hashString(`${layoutKey}:${slot.column}:${slot.row}`),
    }));

  while (remaining.length > 0) {
    let nextIndex = 0;
    for (let index = 1; index < remaining.length; index += 1) {
      const candidate = remaining[index];
      const current = remaining[nextIndex];
      if (
        candidate.minDistance > current.minDistance
        || (
          candidate.minDistance === current.minDistance
          && candidate.tieBreaker > current.tieBreaker
        )
      ) {
        nextIndex = index;
      }
    }

    const [{ slot }] = remaining.splice(nextIndex, 1);
    selected.push(slot);
    for (const candidate of remaining) {
      candidate.minDistance = Math.min(
        candidate.minDistance,
        gridDistanceSquared(candidate.slot, slot),
      );
    }
  }

  return selected;
};

const getDockSlotOrder = ({
  safeScreenWidth,
  safeScreenHeight,
  edge,
  topInset,
  bottomInset,
  distribution,
  layoutKey,
}: {
  safeScreenWidth: number;
  safeScreenHeight: number;
  edge: "top" | "bottom";
  topInset: number;
  bottomInset: number;
  distribution: "packed" | "scattered";
  layoutKey: string;
}): DockSlot[] => {
  const cacheKey = [
    safeScreenWidth,
    safeScreenHeight,
    edge,
    topInset,
    bottomInset,
    distribution,
    layoutKey,
  ].join(":");
  const cached = slotOrderCache.get(cacheKey);
  if (cached) return cached;

  const columnCount = Math.max(
    1,
    Math.floor((safeScreenWidth - DOCK_MARGIN * 2 + DOCK_GAP) / (DOCK_CARD_WIDTH + DOCK_GAP)),
  );
  // Rows fill only the band between the insets (a host's nav above, its footer
  // below), so no tile lands over either.
  const firstRowTop = Math.max(DOCK_MARGIN, topInset);
  const bandBottom = safeScreenHeight - Math.max(DOCK_MARGIN, bottomInset);
  const rowCount = Math.max(
    1,
    Math.floor((bandBottom - firstRowTop + DOCK_GAP) / (DOCK_CARD_HEIGHT + DOCK_GAP)),
  );
  const slots: DockSlot[] = [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let column = 0; column < columnCount; column += 1) {
      slots.push({
        column,
        row,
        x: DOCK_MARGIN + column * (DOCK_CARD_WIDTH + DOCK_GAP),
        y: edge === "top"
          ? firstRowTop + row * (DOCK_CARD_HEIGHT + DOCK_GAP)
          : bandBottom - DOCK_CARD_HEIGHT - row * (DOCK_CARD_HEIGHT + DOCK_GAP),
      });
    }
  }

  const orderedSlots = distribution === "packed"
    ? slots
    : scatterDockSlots(slots, `${layoutKey}:${edge}`);
  if (slotOrderCache.size >= MAX_SLOT_ORDER_CACHE_ENTRIES) {
    const oldestKey = slotOrderCache.keys().next().value;
    if (oldestKey) slotOrderCache.delete(oldestKey);
  }
  slotOrderCache.set(cacheKey, orderedSlots);
  return orderedSlots;
};

export function getWindowStackZIndex({
  isMinimized,
  zIndex,
  score,
}: {
  isMinimized: boolean;
  zIndex?: number;
  score?: number;
}): number {
  if (isMinimized) return MINIMIZED_DOCK_Z_INDEX_BASE;
  const requestedZIndex = typeof zIndex === "number" ? zIndex : 10 + ((score || 5) * 10);
  return Math.max(MIN_ACTIVE_WINDOW_Z_INDEX, requestedZIndex);
}

type DockRect = { x: number; y: number; width: number; height: number };

/** The geometry a dock plan needs from each workspace window. */
export type DockWindow = {
  id: string;
  position?: { x: number; y: number } | null;
  size?: { width: number; height: number } | null;
  isMinimized?: boolean;
};

export type MinimizedDockOptions = {
  screenWidth: number;
  screenHeight: number;
  edge?: "top" | "bottom";
  topInset?: number;
  /** Room kept free along the bottom, like `topInset` along the top. */
  bottomInset?: number;
  distribution?: "packed" | "scattered";
  layoutKey?: string;
};

export type MinimizedDockTile = {
  x: number;
  y: number;
  scale: number;
  width: number;
  height: number;
  zIndex: number;
};

const overlapArea = (left: DockRect, right: DockRect): number => {
  const width = Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x);
  const height = Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y);
  return width > 0 && height > 0 ? width * height : 0;
};

const toOpenWindowRect = (entry: DockWindow): DockRect | null => {
  if (entry.isMinimized || !entry.position || !entry.size) return null;
  const rect = {
    x: entry.position.x,
    y: entry.position.y,
    width: entry.size.width,
    height: entry.size.height,
  };
  return Object.values(rect).every(Number.isFinite) ? rect : null;
};

const planCache = new WeakMap<readonly DockWindow[], Map<string, Map<string, MinimizedDockTile>>>();

/**
 * Assigns every minimized window a dock tile. Each tile has a home slot in the
 * scattered (or packed) slot order, by its place among minimized windows.
 * Minimized tiles sit below every open window, so a tile placed under one would
 * be hidden and impossible to reach. A tile whose home slot an open window
 * covers moves to the first free, clear slot; every other tile stays home.
 * When the desk has no clear slot, the least-covered unused slot wins, and
 * slots are only reused once every slot holds a tile.
 *
 * The plan depends on the open windows, so a tile moves out of the way when a
 * window opens, moves, or resizes over it, and returns home once it is clear.
 */
export function planMinimizedDock(
  windows: readonly DockWindow[],
  {
    screenWidth,
    screenHeight,
    edge = "bottom",
    topInset = DOCK_MARGIN,
    bottomInset = DOCK_MARGIN,
    distribution = "scattered",
    layoutKey = "workspace",
  }: MinimizedDockOptions,
): Map<string, MinimizedDockTile> {
  const safeScreenWidth = Math.max(DOCK_CARD_WIDTH + DOCK_MARGIN * 2, screenWidth);
  const safeScreenHeight = Math.max(DOCK_CARD_HEIGHT + DOCK_MARGIN * 2, screenHeight);
  const optionsKey = [safeScreenWidth, safeScreenHeight, edge, topInset, bottomInset, distribution, layoutKey].join(":");
  const cachedByOptions = planCache.get(windows);
  const cached = cachedByOptions?.get(optionsKey);
  if (cached) return cached;

  const orderedSlots = getDockSlotOrder({
    safeScreenWidth,
    safeScreenHeight,
    edge,
    topInset,
    bottomInset,
    distribution,
    layoutKey,
  });
  const obstacles = windows
    .map(toOpenWindowRect)
    .filter((rect): rect is DockRect => rect !== null);
  const tiles = windows
    .filter((entry) => entry.isMinimized)
    .map((entry, minimizedIndex) => {
      const width = Math.max(1, entry.size?.width ?? DOCK_CARD_WIDTH);
      const height = Math.max(1, entry.size?.height ?? DOCK_CARD_HEIGHT);
      const scale = Math.min(MAX_SCALE, DOCK_CARD_WIDTH / width, DOCK_CARD_HEIGHT / height);
      return {
        id: entry.id,
        homeIndex: minimizedIndex % orderedSlots.length,
        scale,
        width: width * scale,
        height: height * scale,
      };
    });
  const coverAt = (index: number, width: number, height: number): number => {
    const slot = orderedSlots[index];
    const rect = { x: slot.x, y: slot.y, width, height };
    let cover = 0;
    for (const obstacle of obstacles) cover += overlapArea(rect, obstacle);
    return cover;
  };

  const slotUse = new Array<number>(orderedSlots.length).fill(0);
  const assigned = new Map<string, number>();
  // Pass 1: every tile whose home slot is clear stays there, so covering one
  // tile never shuffles the others.
  for (const tile of tiles) {
    if (coverAt(tile.homeIndex, tile.width, tile.height) > 0) continue;
    assigned.set(tile.id, tile.homeIndex);
    slotUse[tile.homeIndex] += 1;
  }
  // Pass 2: only covered tiles move, to the first free and clear slot in dock
  // order. A full desk falls back to the least-used, then least-covered slot.
  for (const tile of tiles) {
    if (assigned.has(tile.id)) continue;
    let bestIndex = tile.homeIndex;
    let bestUse = Number.POSITIVE_INFINITY;
    let bestCover = Number.POSITIVE_INFINITY;
    for (let index = 0; index < orderedSlots.length; index += 1) {
      const use = slotUse[index];
      if (use > bestUse) continue;
      const cover = coverAt(index, tile.width, tile.height);
      if (use < bestUse || cover < bestCover) {
        bestIndex = index;
        bestUse = use;
        bestCover = cover;
        if (use === 0 && cover === 0) break;
      }
    }
    assigned.set(tile.id, bestIndex);
    slotUse[bestIndex] += 1;
  }

  const plan = new Map<string, MinimizedDockTile>();
  for (const tile of tiles) {
    const slot = orderedSlots[assigned.get(tile.id) ?? tile.homeIndex];
    plan.set(tile.id, {
      x: slot.x,
      y: slot.y,
      scale: tile.scale,
      width: tile.width,
      height: tile.height,
      // Dock tiles must remain visible and restorable, but every open window
      // owns the hit-test wherever the two overlap.
      zIndex: MINIMIZED_DOCK_Z_INDEX_BASE,
    });
  }

  const nextByOptions = cachedByOptions ?? new Map<string, Map<string, MinimizedDockTile>>();
  nextByOptions.set(optionsKey, plan);
  if (!cachedByOptions) planCache.set(windows, nextByOptions);
  return plan;
}
